import React, { useRef, useState } from 'react'
import { Photo, File } from 'types/globalTypes'
import './styles.scss'
import filePlus from 'assets/Images/FilePlus.svg'
import { useFileUploadMutation } from 'api/FileUpload/FileUpload'
import ButtonSpinner from '../ButtonSpinner'
import { X } from '@phosphor-icons/react'
import { Progress } from 'antd'

interface DocumentUploadProps {
  documentIds: string[]
  setDocumentIds: React.Dispatch<React.SetStateAction<string[]>>
  fileData?: File
  loading?: boolean
  docType?: 'application/pdf' | 'image/png' | 'image/jpeg' | string
  files?: File[] | Photo[]
  multiple?: boolean
  sourceType?:
    | 'guardian_profile'
    | 'user_profile'
    | 'organization_profile'
    | 'mentor_profile'
    | 'organizer_profile'
    | 'authorized_adult_profile'
    | 'kid_profile'
    | 'amenity'
    | 'live_update'
    | 'other'
    | 'icon'
  // Optional: allow parent components to get local preview URLs (e.g. for images)
  onPreviewChange?: (urls: string[]) => void
}

type UploadItem = {
  id?: string
  file: File | globalThis.File
  name: string
  size: number
  progress: number
  status: 'queued' | 'uploading' | 'done' | 'error'
  cancelToken?: any
}

const DocUploadWithPrgress: React.FC<DocumentUploadProps> = ({
  documentIds,
  setDocumentIds,
  fileData,
  loading,
  docType,
  files,
  multiple,
  sourceType,
  onPreviewChange,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [items, setItems] = useState<UploadItem[]>(
    files
      ? (files as any[]).map((f: any) => ({
          id: f.id,
          file: f,
          name: f.name || f.title || f.fileName || 'file',
          size: f.size || 0,
          progress: 100,
          status: 'done',
        }))
      : [],
  )
  const [uploading, setUploading] = useState(false)

  const { mutate: fileUploadMutation } = useFileUploadMutation()

  const handleChoose = () => {
    if (inputRef.current) inputRef.current.click()
  }

  const onFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files
    if (!selected) return
    const arr = Array.from(selected)
    const newItems: UploadItem[] = arr.map(f => ({
      file: f,
      name: f.name,
      size: f.size,
      progress: 0,
      status: 'queued',
    }))
    setItems(prev => [...prev, ...newItems])

    // Generate local preview URLs for image files if requested
    if (onPreviewChange) {
      const imageUrls = arr
        .filter(f => f.type && f.type.startsWith('image/'))
        .map(f => URL.createObjectURL(f))
      if (imageUrls.length > 0) {
        onPreviewChange(imageUrls)
      }
    }
    // start uploading sequentially or in parallel
    uploadFiles(newItems)
    // reset input value to allow re-upload of same file
    e.currentTarget.value = ''
  }

  const uploadFiles = (uploadList: UploadItem[]) => {
    if (!uploadList || uploadList.length === 0) return
    setUploading(true)
    uploadList.forEach((item, idx) => {
      const form = new FormData()
      form.append('file', item.file as globalThis.File)
      const newFileMeta = {
        title: item.name,
        description: 'uploaded document',
        altText: item.name,
        sourceType: sourceType || 'organizer_profile',
      }

      // update status to uploading
      setItems(prev =>
        prev.map(p =>
          p === item ? { ...p, status: 'uploading', progress: 0 } : p,
        ),
      )

      fileUploadMutation(
        {
          payload: form,
          newFile: newFileMeta,
        },
        {
          onSuccess: (res: any) => {
            console.log(item, 'item,', items)

            setItems(prev =>
              prev.map(p =>
                p.file === item.file
                  ? { ...p, status: 'done', progress: 100, id: res.id }
                  : p,
              ),
            )
            if (res && res.id) setDocumentIds(prev => [...prev, res.id])
            setUploading(false)
          },
          onError: (err: any) => {
            setItems(prev =>
              prev.map(p => (p === item ? { ...p, status: 'error' } : p)),
            )
            setUploading(false)
          },
        },
      )
    })
  }

  const handleRemove = (index: number) => {
    const removed = items[index]
    // If uploaded, remove its id from parent
    if (removed && removed.id) {
      setDocumentIds(prev => prev.filter(i => i !== removed.id))
    }
    const updatedItems = items.filter((_, i) => i !== index)
    setItems(updatedItems)

    // Update previews if requested
    if (onPreviewChange) {
      const urls = updatedItems
        .filter(it => (it.file as any)?.type?.startsWith?.('image/'))
        .map(it => URL.createObjectURL(it.file as globalThis.File))
      onPreviewChange(urls)
    }
  }

  return (
    <div className="mt-20px">
      <div className="upload-component">
        <div className="img">
          <img src={filePlus} width={32} height={32} alt="file-icon" />
        </div>
        <p className="para">PDF, PNG or JPG formats. Max. 10 MB each.</p>
        <button type="button" className="prim-btn" onClick={handleChoose}>
          {uploading ? <ButtonSpinner size={14} color="#fff" /> : 'Upload File'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={docType || '.pdf,.png,.jpg,.jpeg'}
          multiple={multiple || true}
          onChange={onFilesSelected}
          style={{ display: 'none' }}
        />
      </div>

      {items.length > 0 && (
        <div className="upload-list mt-10px">
          {items.map((it, idx) => (
            <div className="upload-item" key={idx}>
              <div className="upload-item-left">
                <img src={filePlus} width={28} height={28} alt="file-icon" />
                <div>
                  <h5 className="upload-name">{it.name}</h5>
                  <p className="upload-size">
                    {(it.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <div className="upload-item-right">
                {it.status === 'uploading' && (
                  <div
                    className="progress"
                    style={{
                      background:
                        'conic-gradient(#8cc73f calc(75 * 1%), #e7e6ef 0)',
                    }}
                  >
                    <div className="inner-circle"></div>
                  </div>
                )}
                {it.status === 'done' && (
                  <button
                    className="remove-btn"
                    onClick={() => handleRemove(idx)}
                  >
                    <X size={24} color="#000" />
                  </button>
                )}
                {it.status === 'error' && (
                  <div className="status error">Error</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default DocUploadWithPrgress
